import { useEffect, useRef } from 'react';
import { CalendarDays, Check, Mail, MessageSquareText, Share2, X } from 'lucide-react';
import type { Prospect } from '@agency/website-schema';
import { formatProspectPrice, formatProspectStatus } from '../proposal-display.js';

type Props = {
  open: boolean;
  prospect: Prospect;
  displayPriceCents: number;
  promotionActive: boolean;
  paymentNote: string;
  onClose: () => void;
  onClaim: () => void;
  checkoutLoading: boolean;
  onSchedule: () => void;
  onShare: () => void;
  onChanges: () => void;
};

const agencyContactEmail = import.meta.env.VITE_AGENCY_CONTACT_EMAIL ?? 'hello@auri79.com';

export function ProposalDetailsSheet({ open, prospect, displayPriceCents, promotionActive, paymentNote, onClose, onClaim, checkoutLoading, onSchedule, onShare, onChanges }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = previous; window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);
  if (!open) return null;

  return <div className="dialog-backdrop" role="presentation" onMouseDown={event => { if (event.currentTarget === event.target) onClose(); }}>
    <section className="details-sheet" role="dialog" aria-modal="true" aria-labelledby="proposal-details-title">
      <div className="sheet-handle" aria-hidden="true" />
      <header><div><p>PROPOSAL DETAILS</p><h2 id="proposal-details-title">{prospect.businessName}</h2></div><button ref={closeRef} type="button" onClick={onClose} aria-label="Close proposal details"><X /></button></header>
      <div className={`sheet-price${promotionActive?' has-promotion':''}`}><span>{promotionActive?'Limited-time investment':'Your investment'}</span><div><strong>{formatProspectPrice(displayPriceCents)}</strong>{promotionActive&&<del>{formatProspectPrice(prospect.priceCents)}</del>}</div><small>{promotionActive?`Save ${formatProspectPrice(prospect.priceCents-displayPriceCents)} before this offer closes · ${paymentNote}`:paymentNote}</small></div>
      <button className="sheet-primary" type="button" aria-busy={checkoutLoading} disabled={checkoutLoading||prospect.status==='purchased'} onClick={onClaim}><Check />{prospect.status==='purchased'?'Payment received':checkoutLoading?'Opening secure checkout…':'Claim This Website'}</button>
      <button className="sheet-action" type="button" onClick={onSchedule}><CalendarDays /><span><strong>Schedule A Call</strong><small>Pick a time that works for you</small></span></button>
      <div className="sheet-status"><span>Proposal status</span><strong><i /> {formatProspectStatus(prospect.status)}</strong></div>
      <button className="sheet-action" type="button" onClick={onChanges}><MessageSquareText /><span><strong>Request Changes</strong><small>Tell us what you&apos;d like adjusted</small></span></button>
      <button className="sheet-action" type="button" onClick={onShare}><Share2 /><span><strong>Share Proposal</strong><small>Copy a secure preview link</small></span></button>
      <a className="sheet-action" href={`mailto:${agencyContactEmail}`}><Mail /><span><strong>Contact Auri79</strong><small>{agencyContactEmail}</small></span></a>
    </section>
  </div>;
}
